import { useFormContext } from 'react-hook-form';
import type { CampaignEntry } from '../api';
import { useCampaigns } from '../api';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import type {
  GridColDef,
  GridColumnHeaderParams,
  GridRenderCellParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import { DataGrid } from '@mui/x-data-grid';
import { Checkbox, ChipInput, CircularProgress, SuspenseErrorFallback } from 'components/common';
import { FormContainer } from 'components/layout';
import { CampaignStatusSwitcher, DuplicateStatusMenu } from './FacebookFormComponents';
import { buildCampaignFetchParams } from '../utils/campaignFetchParams';
import { isSimplifiedFlowProcess } from '../utils/marketingProcess';
import { restoreSelectedCampaignsFromUrl, toCampaignSelectionEntries } from '../utils/campaignUrlSync';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { isEmpty } from 'lodash-es';
import { isErrorWithCause } from 'utils';
import type { MarketingProcess } from 'types/shared';
import { MemoizedCampaignBudgetField } from './CampaignBudgetField';
import { BudgetsHeader } from './TableHeaders';
import { useLocationParams } from 'hooks';


const CAMPAIGNS_LIMIT = 20;

const gridStyles = () => ({
  '.MuiDataGrid-row .MuiInputAdornment-positionEnd': {
    visibility: 'hidden',
  },
  '.MuiDataGrid-row:hover .MuiInputAdornment-positionEnd': {
    visibility: 'visible',
  },
  '& .MuiDataGrid-main': {
    overflow: 'hidden',
  },
});

const createColumns = (
  isSimplifiedFlow: boolean
): GridColDef<CampaignEntry>[] => [
  {
    field: 'id',
    headerName: 'Campaign ID',
    width: 160,
  },
  {
    field: 'name',
    headerName: 'Campaign Name',
    width: 750,
  },
  {
    field: 'budget',
    headerName: 'CBO Budget',
    disableColumnMenu: true,
    width: 240,
    renderCell: (params: GridRenderCellParams<CampaignEntry>) => (
      <MemoizedCampaignBudgetField cellParams={params} />
    ),
    renderHeader: (params: GridColumnHeaderParams<CampaignEntry>) => {
      return (
        <BudgetsHeader
          tooltip="CBO budget is calculated relative to the campaign"
          headerName={params.colDef.headerName}
        />
      );
    },
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 180,
    sortable: false,
    type: 'singleSelect',
    valueOptions: ['ACTIVE', 'PAUSED'],
    renderHeader: (params: GridColumnHeaderParams<CampaignEntry>) => {
      if (!isSimplifiedFlow) {
        return <DuplicateStatusMenu formField="selectedCampaigns" />;
      }
      return params.colDef.headerName;
    },
    renderCell: (
      params: GridRenderCellParams<CampaignEntry, CampaignEntry['status']>
    ) => {
      if (!isSimplifiedFlow) {
        return <CampaignStatusSwitcher cellParams={params} />;
      }
      return params.row.status;
    },
  },
];

const useCampaignSelection = () => {
  const { watch, setValue } = useFormContext();

  const selectedCampaignsRows = watch('selectedCampaigns') as CampaignEntry[];

  const selectedCampaigns = useMemo(
    () => selectedCampaignsRows?.map((c) => c.id) ?? [],
    [selectedCampaignsRows],
  );

  const updateSelectedCampaigns = useCallback(
    (campaigns: CampaignEntry[]) => {
      setValue('selectedCampaigns', campaigns, { shouldValidate: true });
    },
    [setValue],
  );

  return {
    selectedCampaigns,
    selectedCampaignsRows,
    updateSelectedCampaigns,
  };
};

/**
 * Keeps the campaign selection (+ CBO budget/duplicate status per campaign)
 * in sync with the URL. Restoring from the URL has to wait for `rows` since
 * campaigns are fetched asynchronously — unlike creatives/ad accounts, this
 * can't happen at form-default time.
 */
const useCampaignsUrlSync = (
  rows: CampaignEntry[] | undefined,
  isPending: boolean,
  selectedCampaignsRows: CampaignEntry[] | undefined,
  updateSelectedCampaigns: (campaigns: CampaignEntry[]) => void,
) => {
  const { watch, setValue } = useFormContext();
  const selectedCampaignsBudgets = watch('selectedCampaignsBudgets') as
    | Record<string, string>
    | undefined;

  const { selectedCampaigns: selectedCampaignsParam, setSelectedCampaigns: setSelectedCampaignsParam } =
    useLocationParams();

  const hasRestoredFromUrl = useRef(false);
  // Set right before a restore applies `selectedCampaigns`/budgets. The
  // write-back effect below runs in the same commit, before RHF's
  // `setValue` has re-rendered this component with the restored value, so
  // without this it would immediately mirror the still-stale (empty)
  // selection back to the URL and wipe out the param it just restored from.
  const skipNextWriteBack = useRef(false);

  // Runs once, right when the campaigns query first settles (loading ->
  // loaded, even to an empty list), and only if the form doesn't already
  // have a selection (e.g. from a prior visit to this step in the same
  // session). Gating on `isPending` — not just `rows` — matters: while the
  // query is still loading, `rows` is `undefined` and any write-back below
  // would otherwise clear the `campaigns` URL param before it's ever read.
  useEffect(() => {
    if (hasRestoredFromUrl.current || isPending) return;
    hasRestoredFromUrl.current = true;

    if (!selectedCampaignsParam.length || selectedCampaignsRows?.length) return;

    const restored = restoreSelectedCampaignsFromUrl(selectedCampaignsParam, rows ?? []);
    if (!restored.selectedCampaigns.length) return;

    skipNextWriteBack.current = true;
    updateSelectedCampaigns(restored.selectedCampaigns);
    setValue('selectedCampaignsBudgets', restored.selectedCampaignsBudgets, {
      shouldValidate: true,
    });
  }, [isPending, rows, selectedCampaignsParam, selectedCampaignsRows, updateSelectedCampaigns, setValue]);

  useEffect(() => {
    if (!hasRestoredFromUrl.current) return;

    if (skipNextWriteBack.current) {
      skipNextWriteBack.current = false;
      return;
    }

    setSelectedCampaignsParam(
      toCampaignSelectionEntries(selectedCampaignsRows ?? [], selectedCampaignsBudgets),
    );
  }, [isPending, selectedCampaignsRows, selectedCampaignsBudgets, setSelectedCampaignsParam]);
};

/** Mirrors the "search by campaign name" chip filter to the URL. */
const useCampaignNameFilterUrlSync = (filterByName: string[]) => {
  const { setFilterCampaignsByName } = useLocationParams();

  useEffect(() => {
    setFilterCampaignsByName(filterByName ?? []);
  }, [filterByName, setFilterCampaignsByName]);
};

const useWatchedFormValues = () => {
  const { watch } = useFormContext();
  const adAccounts = watch('adAccounts');
  const marketingProcess = watch('marketingProcess');
  const filterCampaignsByName = watch('filterCampaignsByName');
  const selectedCampaignsOnly: boolean = watch('selectedCampaignsOnly');

  return {
    facebookAccountId: (adAccounts ?? []) as string[],
    marketingProcess: marketingProcess as MarketingProcess,
    filterByName: filterCampaignsByName as string[],
    selectedCampaignsOnly,
  };
};

const LoadingOverlay = ({ title }: { title: string }) => (
  <Box
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      display: 'flex',
      alignContent: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      zIndex: 2,
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
    }}
  >
    <CircularProgress title={title} />
  </Box>
);

const DataGridFooter = ({ selectedCount }: { selectedCount: number }) => {
  if (!selectedCount) return null;

  return (
    <Box
      sx={(theme) => ({
        p: 2,
        height: 54,
        minHeight: 54,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderTop: `1px solid ${theme.palette.divider}`,
        cursor: 'default',
      })}
    >
      {selectedCount === CAMPAIGNS_LIMIT ? (
        <>
          <InfoIcon color="primary" sx={{ fontSize: 21 }} />
          <Typography variant="body2">
            {`Up to ${CAMPAIGNS_LIMIT} campaigns can be selected`}
          </Typography>
        </>
      ) : (
        <Typography variant="body2">
          {selectedCount} rows selected
        </Typography>
      )}
    </Box>
  );
};

const EmptyState = ({ message }: { message: string }) => (
  <Typography
    sx={(theme) => ({
      color: theme.palette.grey[400],
      fontSize: theme.typography.h4.fontSize,
    })}
  >
    {message}
  </Typography>
);

const CampaignsGrid = () => {
  const { facebookAccountId, marketingProcess, filterByName, selectedCampaignsOnly } = useWatchedFormValues();
  const { selectedCampaigns, selectedCampaignsRows, updateSelectedCampaigns } = useCampaignSelection();
  const { selectedCampaigns: selectedCampaignsParam } = useLocationParams();

  useCampaignNameFilterUrlSync(filterByName);

  const isSimplifiedFlow = useMemo(
    () => isSimplifiedFlowProcess(marketingProcess),
    [marketingProcess],
  );

  const columns = useMemo(
    () => createColumns(isSimplifiedFlow),
    [isSimplifiedFlow],
  );

  // Campaigns from the URL may sit outside the browse list (name filter /
  // status), so this step also asks for them by id and shows the merged list.
  // Deep-link init only ran the by-id request, so this is where the browse
  // list is fetched for the first time.
  const urlCampaignIds = useMemo(
    () => [...new Set(selectedCampaignsParam.map((campaign) => campaign.id))],
    [selectedCampaignsParam],
  );

  const {
    data: rows,
    isPending,
    isError,
    error,
  } = useCampaigns({
    params: buildCampaignFetchParams(marketingProcess, facebookAccountId, urlCampaignIds),
  });

  useCampaignsUrlSync(rows, isPending, selectedCampaignsRows, updateSelectedCampaigns);

  const filteredRows = useMemo(() => {
    if (!rows?.length && !selectedCampaignsRows?.length) return [];

    const selectedIds = new Set(selectedCampaigns.map((id) => String(id)));
    const mergedRowsMap = new Map<string, CampaignEntry>();

    rows?.forEach((row) => {
      mergedRowsMap.set(String(row.id), row);
    });

    selectedCampaignsRows?.forEach((row) => {
      if (!mergedRowsMap.has(String(row.id))) {
        mergedRowsMap.set(String(row.id), row);
      }
    });

    return [...mergedRowsMap.values()]
      .filter((row) => {
        const isSelected = selectedIds.has(String(row.id));

        // Keep selected campaigns visible even when search query does not match.
        if (isSelected) {
          return true;
        }

        return !filterByName?.length || filterByName.every((filterValue) =>
          row.name.toLowerCase().includes(filterValue.toLowerCase()),
        );
      })
      .filter((row) => !selectedCampaignsOnly || selectedIds.has(String(row.id)));
  }, [rows, selectedCampaignsRows, selectedCampaignsOnly, selectedCampaigns, filterByName]);

  const visibleCampaignIds = useMemo(
    () => new Set(filteredRows.map((row) => String(row.id))),
    [filteredRows],
  );

  const safeSelectedCampaigns = useMemo(
    () => selectedCampaigns.filter((id) => visibleCampaignIds.has(String(id))),
    [selectedCampaigns, visibleCampaignIds],
  );

  const handleSelectionMode = useCallback(
    (selectedRows: GridRowSelectionModel) => {
      if (!rows) return;

      const parsedSelectedRows = selectedRows.slice(0, CAMPAIGNS_LIMIT);

      const visibleRowsMap = new Map(filteredRows.map(row => [String(row.id), row]));
      const selectedRowsMap = new Map(
        selectedCampaignsRows?.map(row => [String(row.id), row]) ?? [],
      );

      const currentSelected = parsedSelectedRows
        .map(id => {
          const row = visibleRowsMap.get(String(id));
          if (!row) return null;

          return {
            ...row,
            status: selectedRowsMap.get(String(id))?.status ?? row.status,
          };
        })
        .filter((campaign): campaign is CampaignEntry => campaign !== null);

      const previousSelected = selectedCampaignsRows?.filter(
        (campaign) =>
          !visibleRowsMap.has(String(campaign.id)),
      ) ?? [];

      const allSelectedCampaigns = [...previousSelected, ...currentSelected];
      updateSelectedCampaigns(allSelectedCampaigns);
    },
    [rows, filteredRows, selectedCampaignsRows, updateSelectedCampaigns],
  );

  if (!marketingProcess) {
    return <EmptyState message="Marketing process is required" />;
  }

  if (isError) {
    const errorMessage = isErrorWithCause(error)
      ? error?.cause?.error ?? error?.message ?? 'An error occurred while fetching campaigns'
      : 'An error occurred while fetching campaigns';

    return (
      <SuspenseErrorFallback
        title="Choose Campaigns"
        message={errorMessage}
      />
    );
  }

  if (isPending && isEmpty(filteredRows)) {
    return <CircularProgress title="Campaigns are loading..." />;
  }

  if (isEmpty(filteredRows)) {
    return <EmptyState message="No campaigns found" />;
  }

  return (
    <Box style={{ position: 'absolute', left: 0, top: 0, overflow: 'auto', height: '100%', width: '100%' }}>
      {isPending && <LoadingOverlay title="Campaigns are loading..." />}

      <DataGrid
        rows={filteredRows}
        columns={columns}
        checkboxSelection
        rowSelectionModel={safeSelectedCampaigns}
        onRowSelectionModelChange={handleSelectionMode}
        hideFooterPagination
        disableColumnMenu
        getRowId={({ id }) => id}
        isRowSelectable={(params) =>
          selectedCampaigns.length < CAMPAIGNS_LIMIT ||
          selectedCampaigns.includes(params.row.id)
        }
        slots={{
          footer: () => <DataGridFooter selectedCount={selectedCampaigns.length} />,
        }}
        sx={gridStyles}
      />
    </Box>
  );
};

const CampaignsForm = () => {
  return (
    <FormContainer
      title="Choose campaigns"
      sx={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2, height: '100%' }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            maxWidth: '100%',
            minWidth: '50%',
            flexDirection: 'row',
            alignItems: 'start',
            flexWrap: 'nowrap',
          }}
        >
          <ChipInput
            name="filterCampaignsByName"
            inputLabel="Enter campaign name"
          />
          <Checkbox
            dataTestId={'selectedCampaignsOnly'}
            name={'selectedCampaignsOnly'}
            label={'Selected only'}
            controlLabelSx={{ whiteSpace: 'nowrap' }}
          />
        </Box>
      </Box>
      <Box sx={{flex: 1, height: '100%', width: '100%', position: 'relative' }}>
        <CampaignsGrid />
      </Box>
    </FormContainer>
  );
};

export default CampaignsForm;